/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1700006400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Nov 15 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "gcs-source",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Cloud credential setup",
          "depth": 0,
          "id": "cloud-credential-setup"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Configure your service account",
          "depth": 1,
          "id": "configure-your-service-account"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "CSV requirements",
          "depth": 1,
          "id": "csv-requirements"
        }, {
          "value": "JSON requirements",
          "depth": 1,
          "id": "json-requirements"
        }, {
          "value": "Model setup",
          "depth": 1,
          "id": "model-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      code: "code",
      ul: "ul",
      li: "li",
      ol: "ol"
    }, _provideComponents(), props.components), {NoCommonErrors} = _components;
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data from CSV and JSON files stored in Google Cloud Storage (GCS) and push them to downstream destinations. To get started, you need a GCS bucket and Google Cloud credentials."
      }), "\n", _jsx(_components.h2, {
        id: "cloud-credential-setup",
        children: "Cloud credential setup"
      }), "\n", _jsxs(_components.p, {
        children: ["See the guide for ", _jsx(_components.a, {
          href: "/security/gcp",
          children: "configuring GCP credentials"
        }), " to learn how you can set up a service account and use its credentials. The IAM user whose credentials you use must have programmatic access enabled and permission to read from the Cloud Storage path you want to use."]
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select Google Cloud Storage and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-service-account",
        children: "Configure your service account"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the credentials you previously created or click ", _jsx(_components.strong, {
          children: "Create new"
        }), " to set them up now."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsxs(_components.p, {
        children: ["Enter the ", _jsx(_components.strong, {
          children: "Project ID"
        }), " or your GCS project and the ", _jsx(_components.strong, {
          children: "Bucket name"
        }), ". The bucket name should be just the name of the bucket, not a URL."]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up GCS as a source for the first time, Hightouch validates your GCP credentials and access to your GCS bucket. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from your GCS bucket. The file in your GCS bucket must have either a ", _jsx(_components.code, {
          children: ".csv"
        }), " or ", _jsx(_components.code, {
          children: ".json"
        }), " extension."]
      }), "\n", _jsx(_components.h3, {
        id: "csv-requirements",
        children: "CSV requirements"
      }), "\n", _jsx(_components.p, {
        children: "CSV files must meet the following requirements:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["They must have a ", _jsx(_components.code, {
            children: ".csv"
          }), " extension."]
        }), "\n", _jsx(_components.li, {
          children: "They must have a header row. The values in the header row are automatically available as column names when you set up a sync."
        }), "\n", _jsx(_components.li, {
          children: "They must use comma-separated values; tabs and other delimiters aren't supported."
        }), "\n", _jsxs(_components.li, {
          children: ["They must use double quotes (", _jsx(_components.code, {
            children: "\""
          }), ") for quoted values."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "json-requirements",
        children: "JSON requirements"
      }), "\n", _jsx(_components.p, {
        children: "JSON files must meet the following requirements:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["They must have a ", _jsx(_components.code, {
            children: ".json"
          }), " extension."]
        }), "\n", _jsx(_components.li, {
          children: "The input file must contain an array at the top level."
        }), "\n", _jsx(_components.li, {
          children: "Each element in the input file must be an object with the same keys."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The keys of the array elements are automatically available as column names when you set up a sync."
      }), "\n", _jsx(_components.h3, {
        id: "model-setup",
        children: "Model setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models?from=docs",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview page"]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add model."
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select the GCS source you previously created."
        }), "\n", _jsxs(_components.li, {
          children: ["Enter the ", _jsx(_components.strong, {
            children: "relative path"
          }), " to the CSV or JSON file that you want to sync data from, like ", _jsx(_components.code, {
            children: "path/file.csv"
          }), ". The path shouldn't contain the bucket name."]
        }), "\n", _jsx(_components.li, {
          children: "Preview your model's query results."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name your model and select its ", _jsx(_components.a, {
            href: "/models/creating-models#unique-primary-key-requirement",
            children: _jsx(_components.strong, {
              children: "primary key"
            })
          }), ". Hightouch uses the primary key to determine which rows have been added, changed, or removed since the last sync."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(NoCommonErrors, {
        source: "true"
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
